/**
 * Leftmost row action menu for datagrids: a more-vert icon button that
 * opens a context menu with the row operations (show / edit / delete).
 * Delete asks for confirmation and runs pessimistically.
 */

import { useState, MouseEvent } from 'react';
import {
  Confirm,
  useCreatePath,
  useDelete,
  useNotify,
  useRecordContext,
  useRefresh,
  useResourceContext,
  useTranslate,
} from 'react-admin';
import { useNavigate } from 'react-router-dom';
import { IconButton, ListItemIcon, ListItemText, Menu, MenuItem } from '@mui/material';
import {
  MoreVert as MoreVertIcon,
  EditOutlined as EditIcon,
  VisibilityOutlined as ShowIcon,
  DeleteOutline as DeleteIcon,
  SwitchAccountRounded as ImpersonateIcon,
} from '@mui/icons-material';
import { getCurrentRole, getCurrentUserId } from '@/utils/auth';
import { canImpersonate, roleNameOf, startImpersonation } from '@/utils/impersonation';
import { resolvePlanModules } from '@/utils/planModules';

interface RowActionsMenuProps {
  hasShow?: boolean;
  hasEdit?: boolean;
  hasDelete?: boolean;
  /** Column header (read by Datagrid from the element props) */
  label?: string;
  /** Read by Datagrid from the element props */
  sortable?: boolean;
}

export const RowActionsMenu = ({
  hasShow = false,
  hasEdit = true,
  hasDelete = true,
}: RowActionsMenuProps) => {
  const record = useRecordContext();
  const resource = useResourceContext();
  const createPath = useCreatePath();
  const navigate = useNavigate();
  const translate = useTranslate();
  const notify = useNotify();
  const refresh = useRefresh();
  const [deleteOne, { isLoading: deleting }] = useDelete();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [impersonating, setImpersonating] = useState(false);

  if (!record || !resource) return null;

  const stop = (event: MouseEvent) => {
    event.stopPropagation();
    event.preventDefault();
  };

  const goTo = (type: 'show' | 'edit') => (event: MouseEvent) => {
    stop(event);
    setAnchorEl(null);
    navigate(createPath({ resource, id: record.id, type }));
  };

  // Only user-shaped records (organizations/clients/admins/staff/orgManagers,
  // all backed by /users) carry role_id + user_id; every other resource
  // (departments, qrcodes, plans...) is never an impersonation target.
  const isUserRecord = typeof record.role_id === 'number' && typeof record.user_id === 'number';
  const showImpersonate =
    isUserRecord &&
    canImpersonate(getCurrentRole(), getCurrentUserId(), {
      user_id: record.user_id,
      role_id: record.role_id,
      status: record.status,
    });

  const handleImpersonate = async (event: MouseEvent) => {
    stop(event);
    setAnchorEl(null);
    setImpersonating(true);
    try {
      const planModules = await resolvePlanModules({
        role_id: record.role_id,
        plan_id: record.plan_id,
      });
      startImpersonation({
        sub: String(record.user_id),
        displayName: record.full_name || record.email || String(record.user_id),
        role: roleNameOf(record.role_id) ?? '',
        roleId: record.role_id,
        planModules,
      });
      // Full reload from the root: the effective role changed, so every
      // mounted admin view is stale.
      window.location.hash = '#/';
      window.location.reload();
    } finally {
      setImpersonating(false);
    }
  };

  const handleDelete = () => {
    deleteOne(
      resource,
      { id: record.id, previousData: record },
      {
        mutationMode: 'pessimistic',
        onSuccess: () => {
          notify('ra.notification.deleted', {
            type: 'info',
            messageArgs: { smart_count: 1 },
          });
          refresh();
        },
        onError: (error: any) => {
          notify(error?.message || 'ra.notification.http_error', { type: 'error' });
        },
      }
    );
    setConfirmOpen(false);
  };

  return (
    <>
      <IconButton
        size="small"
        onClick={(event) => {
          stop(event);
          setAnchorEl(event.currentTarget);
        }}
        aria-label={translate('common.row_actions', { _: 'Acciones' })}
      >
        <MoreVertIcon fontSize="small" />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        onClick={(event) => event.stopPropagation()}
      >
        {hasShow && (
          <MenuItem onClick={goTo('show')}>
            <ListItemIcon>
              <ShowIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>{translate('ra.action.show')}</ListItemText>
          </MenuItem>
        )}
        {hasEdit && (
          <MenuItem onClick={goTo('edit')}>
            <ListItemIcon>
              <EditIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>{translate('ra.action.edit')}</ListItemText>
          </MenuItem>
        )}
        {showImpersonate && (
          <MenuItem onClick={handleImpersonate} disabled={impersonating}>
            <ListItemIcon>
              <ImpersonateIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>
              {translate('common.impersonation.action', { _: 'Suplantar identidad' })}
            </ListItemText>
          </MenuItem>
        )}
        {hasDelete && (
          <MenuItem
            onClick={(event) => {
              stop(event);
              setAnchorEl(null);
              setConfirmOpen(true);
            }}
            disabled={deleting}
          >
            <ListItemIcon>
              <DeleteIcon fontSize="small" color="error" />
            </ListItemIcon>
            <ListItemText sx={{ color: 'error.main' }}>
              {translate('ra.action.delete')}
            </ListItemText>
          </MenuItem>
        )}
      </Menu>
      <Confirm
        isOpen={confirmOpen}
        loading={deleting}
        title="ra.message.delete_title"
        content="ra.message.delete_content"
        translateOptions={{ name: translate(`resources.${resource}.name`, { smart_count: 1 }), id: record.id }}
        onConfirm={handleDelete}
        onClose={() => setConfirmOpen(false)}
      />
    </>
  );
};

export default RowActionsMenu;
